import { useCallback } from 'react';
import { doc, DocumentReference, DocumentSnapshot } from 'firebase/firestore';
import { db, auth } from '../constants/firebaseModular';
import type { BetaAccessRequest, BetaFeatureName } from '@usacsmideo/types';
import { useFirestoreListener } from './useFirestoreListener';

/**
 * Live view of a patient's beta-access request for a single feature, read from
 * `users/{patientId}/betaRequests/{featureName}`. Drives the My MIDEO Family
 * beta gate (none → request, requested → pending, declined → re-request).
 * Listener so the pending/declined state flips without a reload. (CU-86aj8k2b9)
 */
function useBetaRequest(
  patientId: string,
  featureName: BetaFeatureName,
  { enabled = true }: { enabled?: boolean } = {},
) {
  // Defense-in-depth: only listen for the authenticated user's own request.
  const uidMatch = auth.currentUser?.uid === patientId;

  const transform = useCallback(
    (snapshot: DocumentSnapshot): BetaAccessRequest | null =>
      snapshot.exists() ? (snapshot.data() as BetaAccessRequest) : null,
    [],
  );

  return useFirestoreListener<DocumentReference, BetaAccessRequest | null>({
    target: doc(db, 'users', patientId, 'betaRequests', featureName),
    queryKey: ['betaRequest', patientId, featureName],
    label: 'betaRequest',
    enabled: enabled && uidMatch,
    transform,
  });
}

export default useBetaRequest;
